import { useState, useRef } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import IconNB from './IconNB';

interface TimePickerNBProps {
  label?: string;
  value?: string; // HH:MM format
  onChange?: (value: string) => void;
  error?: string;
  placeholder?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '15', '30', '45'];

export default function TimePickerNB({
  label,
  value,
  onChange,
  error,
  placeholder = 'Select time',
}: TimePickerNBProps) {
  const [isOpen, setIsOpen] = useState(false);
  // Hour the user has picked in the current session but not yet confirmed with a minute.
  // Lets the user change their mind before the picker emits onChange and closes.
  const [pendingHour, setPendingHour] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedHour = value ? value.split(':')[0] : null;
  const selectedMinute = value ? value.split(':')[1] : null;
  const effectiveHour = pendingHour ?? selectedHour;

  // Close on outside click
  useClickOutside(containerRef, () => {
    setIsOpen(false);
    setPendingHour(null);
  }, isOpen);

  const handleHourClick = (hour: string) => {
    // Stash the hour; do not emit onChange or close yet — the user still picks minutes.
    setPendingHour(hour);
  };

  const handleMinuteClick = (minute: string) => {
    if (onChange && effectiveHour) {
      onChange(`${effectiveHour}:${minute}`);
    }
    setIsOpen(false);
    setPendingHour(null);
  };

  return (
    <div className="flex flex-col gap-1 relative" ref={containerRef}>
      {label && (
        <label className="font-nb font-bold text-sm text-nb-black">
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setIsOpen(prev => {
          if (prev) setPendingHour(null);
          return !prev;
        })}
        className={`
          bg-nb-white
          border-3 border-nb-black
          px-4 py-3
          font-nb
          flex items-center gap-2
          w-full text-left
          focus:border-nb-yellow
          focus:outline-none
          ${error ? 'border-nb-red' : ''}
        `.replace(/\s+/g, ' ').trim()}
      >
        <IconNB name="chevronRight" size={16} className="rotate-[-90deg]" />
        <span className={value ? 'text-nb-black' : 'text-nb-text-muted'}>
          {value || placeholder}
        </span>
      </button>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 bg-nb-white border-3 border-nb-black shadow-[4px_4px_0px_#000] z-50 p-3 w-64"
          role="listbox"
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-3">
            <span className="font-nb font-bold text-sm">Hours</span>
            <span className="font-nb font-bold text-sm">Minutes</span>
          </div>

          {/* Two column layout */}
          <div className="flex gap-4 max-h-48 overflow-y-auto">
            {/* Hours column */}
            <div className="flex-1">
              <div className="grid grid-cols-4 gap-1">
                {HOURS.map(hour => (
                  <button
                    key={hour}
                    type="button"
                    onClick={() => handleHourClick(hour)}
                    className={`
                      w-10 h-10
                      flex items-center justify-center
                      text-sm font-nb
                      border-2 border-transparent
                      hover:border-nb-black
                      hover:bg-nb-yellow
                      ${effectiveHour === hour ? 'bg-nb-black text-white' : ''}
                    `.replace(/\s+/g, ' ').trim()}
                  >
                    {hour}
                  </button>
                ))}
              </div>
            </div>

            {/* Minutes column */}
            <div className="flex-1">
              <div className="flex flex-col gap-1">
                {MINUTES.map(minute => (
                  <button
                    key={minute}
                    type="button"
                    onClick={() => handleMinuteClick(minute)}
                    disabled={!effectiveHour}
                    className={`
                      w-full h-10
                      flex items-center justify-center
                      text-sm font-nb
                      border-2 border-transparent
                      hover:border-nb-black
                      hover:bg-nb-yellow
                      ${selectedMinute === minute ? 'bg-nb-black text-white' : ''}
                      ${!effectiveHour ? 'opacity-30 cursor-not-allowed' : ''}
                    `.replace(/\s+/g, ' ').trim()}
                  >
                    {minute}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {error && (
        <span className="text-xs text-nb-red">{error}</span>
      )}
    </div>
  );
}